import '@common/testing/components/TefComsSpidSelect.js';
import { getBackendUri } from '@common/testing/getBackendUri.js';
import { createTestBearerToken, isCorrectUrlForWebCrypto } from '@common/testing/index.js';
import { components } from '@src/testpage/components.js';
import { TefComs } from '@src/types/typings.d.js';

const { createApp, defineComponent } = Vue;

const API_DIRECT_DOMAIN = 'api-4p.high.coms-dev.aws.de.pri.o2.com';

const shortcuts = {
  apiUrlGateway: [
    { value: 'https://ict.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'ict' },
    { value: 'https://e2e2.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'e2e2' },
    { value: 'https://e2e1.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'e2e1' },
    { value: 'https://perf.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'nfr' },
    { value: 'https://prod.high.apigw-prod.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'prod' },
    { value: 'https://api-consent-coms.telefonica.de/gateway/4p-extra-services/v1', label: 'prod/extern' },
  ],
  apiUrlDirect: [
    { value: 'http://localhost:3200', label: 'api4p@localhost' },
    { value: 'http://localhost:8080', label: 'wiremock@localhost' },
    { value: `https://ict-${API_DIRECT_DOMAIN}`, label: 'ict' },
    { value: `https://e2e2-${API_DIRECT_DOMAIN}`, label: 'e2e2' },
    { value: `https://e2e1-${API_DIRECT_DOMAIN}`, label: 'e2e1' },
    { value: `https://nfr-${API_DIRECT_DOMAIN}`, label: 'nfr' },
  ],
  themeBaseUrl: [
    { value: 'https://dev-library.telefonica.de/1', label: 'occl/o2/dev', color: 'text-bg-primary' },
    { value: 'https://dev-library.telefonica.de/epos', label: 'occl/epos/dev', color: 'text-bg-primary' },
    { value: 'https://library.telefonica.de/1', label: 'occl/o2/prod', color: 'text-bg-secondary' },
    { value: 'https://library.telefonica.de/epos', label: 'occl/epos/prod', color: 'text-bg-secondary' },
  ],
};

const environmentMatch = /(^.*)-micro-frontend-proxy/.exec(document.location.host);
if (environmentMatch) {
  const environment = environmentMatch[1];
  const finalApiUrl = `https://${environment}-${API_DIRECT_DOMAIN}`;
  if (!shortcuts.apiUrlDirect.map((u) => u.value).includes(finalApiUrl)) {
    shortcuts.apiUrlDirect.push({ value: finalApiUrl, label: environment });
  }
}

const search = new URLSearchParams(window.location.search);

const currentApiUrl = search.get('apiUrl')
  ?? getBackendUri({ backend: 'api-4p', gatewayContext: '/4p-extra-services{-env}/v1' });

const params = {
  accessToken: search.get('accessToken') ?? '',
  spId: search.get('spId') ?? 'SP901',
  lcId: search.get('lcId') ?? '4711',
  apiUrl: currentApiUrl,
  theme: search.get('theme') ?? 'occl',
  themeBaseUrl: search.get('themeBaseUrl') ?? shortcuts.themeBaseUrl[0].value,
  traceid: search.get('traceid') ?? '',
};

interface CreateMicrofrontendOptions {
  accessToken?: string;
  spId: string;
  lcId: string;
  apiUrl: string;
  theme: string;
  themeBaseUrl?: string;
  tefOne?: string;
  tefOneEnv?: string;
  traceid?: string;
  mode?: string;
}

/**
 * Creates a microfrontend given the current configuration
 */
const createMicroFrontend = ({
  accessToken = '', spId = '', lcId = '', apiUrl = '', theme = 'occl', themeBaseUrl = '', traceid = '',
}: CreateMicrofrontendOptions) => {
  const mf = document.createElement('tef-coms4cs4p');

  const setAttributes = (attrs: Record<string, string | undefined>) => Object
    .entries(attrs)
    .filter(([, value]) => value) // ignore '', false, undefined
    .forEach(([name, value]) => mf.setAttribute(name, value!));

  setAttributes({
    id: 'consents',
    'access-token': accessToken,
    'sp-id': spId,
    'lc-id': lcId,
    'api-url': apiUrl,
    theme,
    'theme-base-url': themeBaseUrl,
    traceid,
  });
  document.getElementById('mf')?.replaceChildren(mf);
};

/**
 * The VUE app managing the testpage interaction
 */
const TestApp = defineComponent({
  components,

  data() {
    return {
    // definitions
      shortcuts,
      // attributes
      ...params,
      // runtime props
      ready: false,
      info: 'Messages:',
      error: false as boolean | string | Error,
    };
  },

  mounted() {
    this.init();
    this.create();
  },

  methods: {
    init() {
      const componentInstance = this; // eslint-disable-line
      document.addEventListener('tef-coms4cs4p', (event: Event) => {
        const { detail } = event as TefComs.Event;
        const { name, level, error } = detail ?? {};
        console.log('[tef-coms4cs4p] Received \'%s\' event with details %o', name, detail);
        switch (name) {
          case 'load/READY':
            componentInstance.ready = true;
            componentInstance.setInfo('load/READY: The micro-frontend is ready.');
            break;
          case 'consents/EMPTY':
            componentInstance.setInfo('consents/EMPTY: No consents found for this customer');
            break;
          case 'consents/SAVE':
            componentInstance.setInfo('consents/SAVE: Consents saved sucessfully');
            break;
          case 'action/CANCEL':
            componentInstance.setInfo('action/CANCEL: CANCEL button pressed');
            break;
          default:
        }

        if (level === 'error' || error) {
          componentInstance.setError(error ?? detail.message ?? 'an error occurred');
        }
      });
    },

    reload() {
      const query = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        // @ts-expect-error "for testing purposes"
        query.set(key, this.$data[key] as string ?? '');
      });
      window.location.href = `${window.location.pathname}?${query.toString()}`; // reload page
    },

    create() {
      try {
        createMicroFrontend(this);
      } catch (error) {
        this.setError(error as Error);
      }
    },

    showHtml() {
      const escapeHtml = (unsafe: string) => unsafe
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      const html = document.getElementById('consents')?.outerHTML;
      this.setInfo(html ? escapeHtml(html) : 'Element not found.');
    },

    async setTestToken() {
      if (!isCorrectUrlForWebCrypto()) {
        this.setError('Creating a token only works within a secure context. Please use "localhost" as a hostname or https to access this page.');
        return;
      }
      this.accessToken = await createTestBearerToken();
    },

    setInfo(text: string) {
      this.info = [this.info].concat(text).join('<br />');
      this.error = false;
    },

    setError(error: Error | string) {
      this.error = error;
      if (error instanceof Error) {
        const { message, stack } = error;
        const stackText = stack ? `<br />${stack}` : '';
        this.info = `${message}${stackText}`;
      } else {
        this.info = error;
      }
    },

    setValue<T = unknown>(prop: string, value: T) {
      // @ts-expect-error "for testing purposes"
      this.$data[prop] = value;
    },
  },
});

const app = createApp(TestApp);
app.mount('#app');
